/**
 * Gmail-style visual treatment for email recipient inputs (To / Cc / Bcc /
 * Forward). Drop-in `styles` prop and `formatOptionLabel` renderer for the
 * existing `CreatableSelect` / `ReactCreatableSelectWrapper` components.
 *
 * Chips render as name-only green brand pills; dropdown options render as an
 * avatar circle + name/email row.
 */

import { colors } from 'styles/tokens'
import { parseEmailOption } from './helpers'

export { RecipientRow, CcBccToggles } from './RecipientRow'
export {
  buildReplyAllCcOptions,
  filterSelf,
  looksLikeEmail,
  toWireRecipients,
} from './helpers'
export type { ComposeMode, ReplyMode, WireRecipient } from './helpers'

export interface EmailRecipientOption {
  label: string
  value: string
}

export const emailRecipientStyles = {
  multiValue: (provided: Record<string, unknown>) => ({
    ...provided,
    backgroundColor: colors['surface-hover'],
    borderRadius: '9999px',
    paddingLeft: '6px',
    paddingRight: '2px',
    margin: '2px 4px 2px 0',
    // Cap chip width so very long emails truncate to an ellipsis (the
    // `title` attr on chips still shows the full value on hover).
    maxWidth: '240px',
  }),
  multiValueLabel: (provided: Record<string, unknown>) => ({
    ...provided,
    color: colors['brand-active'],
    fontSize: '13px',
    fontWeight: 500,
    padding: '2px 2px 2px 0',
    paddingLeft: 0,
    // react-select's default already has overflow/ellipsis; explicit for clarity.
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  multiValueRemove: (provided: Record<string, unknown>) => ({
    ...provided,
    color: colors['brand-active'],
    borderRadius: '9999px',
    padding: '0 4px',
    marginLeft: '2px',
    ':hover': {
      backgroundColor: colors['surface-active'],
      color: colors.brand,
    },
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isFocused: boolean; isSelected: boolean }
  ) => ({
    ...provided,
    backgroundColor: state.isFocused ? colors['surface-hover'] : colors.surface,
    color: colors.fg,
    cursor: 'pointer',
    padding: '8px 12px',
  }),
}

export function formatEmailRecipientOption(
  option: EmailRecipientOption,
  { context }: { context: 'menu' | 'value' }
) {
  const { name, email } = parseEmailOption(option)

  if (context === 'value') return name || email

  const initial = (name || email)[0]?.toUpperCase() || '?'

  return (
    <div className="flex items-center gap-3 w-full">
      <div className="size-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-medium text-brand-active">{initial}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-fg truncate font-medium">
          {name || email}
        </div>
        {name && (
          <div className="text-xs text-fg-secondary truncate">{email}</div>
        )}
      </div>
    </div>
  )
}
